import React, { useState, useEffect } from 'react';
import { X, Store, Search, Calendar, Gauge, Fuel, Check } from 'lucide-react';
import api from '../services/api';
import { STOCK_STATUS } from '../constants/vehicleStatus';
import { formatPrice } from '../utils/formatters';
import LoadingSpinner from './LoadingSpinner';

const AddToMarketplaceModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1 = pick vehicle, 2 = configure listing
  const [vehicles, setVehicles] = useState([]);
  const [marketplaceVehicleIds, setMarketplaceVehicleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Step 2 state
  const [proPrice, setProPrice] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [vehiclesRes, idsRes] = await Promise.all([
          api.get('/vehicles'),
          api.get('/marketplace/my-vehicle-ids')
        ]);
        setVehicles(vehiclesRes.data.vehicles || []);
        setMarketplaceVehicleIds(idsRes.data.vehicle_ids || []);
      } catch (err) {
        setError('Erreur lors du chargement des véhicules');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const availableVehicles = vehicles.filter(v => {
    const alreadyListed = marketplaceVehicleIds.includes(v.id);
    const matchSearch = !search ||
      `${v.make} ${v.model} ${v.version || ''}`.toLowerCase().includes(search.toLowerCase());
    return !alreadyListed && v.status !== 'sold' && v.stock_status !== STOCK_STATUS.SOLD && matchSearch;
  });

  const alreadyListedCount = vehicles.filter(v => marketplaceVehicleIds.includes(v.id)).length;

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setProPrice(vehicle.price || '');
    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.post('/marketplace/listings', {
        vehicle_id: selectedVehicle.id,
        pro_price: proPrice ? parseInt(proPrice) : null,
        description: description || null,
        notes: notes || null,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise en vente');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-card rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Store className="h-5 w-5 text-carmin" />
            {step === 1 ? 'Choisir un véhicule' : 'Configurer l\'annonce'}
          </h2>
          <button onClick={onClose} className="text-muted hover:text-primary hover:bg-dark-hover rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8"><LoadingSpinner size="lg" /></div>
        ) : step === 1 ? (
          /* Step 1: Vehicle picker */
          <div className="flex flex-col flex-1 min-h-0">
            <div className="p-4 border-b border-dark-border flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher dans mon stock..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-dark-input border border-dark-border text-primary rounded-md text-sm focus:ring-2 focus:ring-carmin placeholder-muted"
                />
              </div>
              <p className="text-xs text-muted mt-2">
                {availableVehicles.length} véhicule{availableVehicles.length !== 1 ? 's' : ''} disponible{availableVehicles.length !== 1 ? 's' : ''}
                {alreadyListedCount > 0 && ` (${alreadyListedCount} déjà sur la marketplace)`}
              </p>
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {availableVehicles.length === 0 ? (
                <div className="text-center py-8 text-muted">
                  <Store className="h-12 w-12 mx-auto mb-3 text-dimmed" />
                  <p>Aucun véhicule disponible</p>
                  <p className="text-xs mt-1">Tous vos véhicules sont déjà sur la marketplace ou vendus.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableVehicles.map(v => (
                    <button
                      key={v.id}
                      onClick={() => handleSelectVehicle(v)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-dark-border hover:border-carmin hover:bg-dark-hover transition-all text-left"
                    >
                      <div className="w-20 h-14 flex-shrink-0 rounded overflow-hidden bg-dark-surface">
                        {v.images?.[0] ? (
                          <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted text-xs">?</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-primary truncate">{v.make} {v.model}</p>
                        <div className="flex gap-3 text-xs text-muted mt-0.5">
                          <span className="inline-flex items-center"><Calendar className="h-3 w-3 mr-1" />{v.year}</span>
                          {v.mileage != null && <span className="inline-flex items-center"><Gauge className="h-3 w-3 mr-1" />{v.mileage?.toLocaleString('fr-BE')} km</span>}
                          {v.fuel_type && <span className="inline-flex items-center"><Fuel className="h-3 w-3 mr-1" />{v.fuel_type}</span>}
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-carmin flex-shrink-0">{formatPrice(v.price)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Step 2: Configure listing */
          <div className="p-4 overflow-y-auto flex-1">
            {/* Selected vehicle summary */}
            <div className="flex items-center gap-3 p-3 bg-carmin-50 rounded-lg mb-4 border border-dark-border">
              {selectedVehicle.images?.[0] && (
                <img src={selectedVehicle.images[0]} alt="" className="w-16 h-12 rounded object-cover" />
              )}
              <div className="flex-1">
                <p className="font-medium text-primary">{selectedVehicle.make} {selectedVehicle.model}</p>
                <p className="text-sm text-secondary">{selectedVehicle.year} - Prix actuel : {formatPrice(selectedVehicle.price)}</p>
              </div>
              <button onClick={() => setStep(1)} className="text-xs text-carmin hover:text-carmin-light">
                Changer
              </button>
            </div>

            <div className="space-y-4">
              {/* Pro price TVAC */}
              <div>
                <label className="block text-sm font-medium text-label mb-1">Prix de vente TVAC (€)</label>
                <input
                  type="number"
                  value={proPrice}
                  onChange={(e) => setProPrice(e.target.value)}
                  placeholder="Prix entre professionnels"
                  className="w-full px-3 py-2 bg-dark-input border border-dark-border text-primary rounded-md focus:ring-2 focus:ring-carmin placeholder-muted"
                />
                <p className="text-xs text-muted mt-1">Prix toutes taxes comprises pour les professionnels</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-label mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez le véhicule, ses points forts, son état..."
                  maxLength={2000}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-input border border-dark-border text-primary rounded-md focus:ring-2 focus:ring-carmin placeholder-muted"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-label mb-1">Notes pour les professionnels</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Historique, raison de la vente, marge possible..."
                  maxLength={1000}
                  rows={2}
                  className="w-full px-3 py-2 bg-dark-input border border-dark-border text-primary rounded-md focus:ring-2 focus:ring-carmin placeholder-muted"
                />
              </div>

              {error && <div className="bg-red-900/20 text-red-400 p-3 rounded-md text-sm border border-red-900/30">{error}</div>}
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && step === 2 && (
          <div className="flex gap-3 p-4 border-t border-dark-border flex-shrink-0">
            <button
              onClick={() => setStep(1)}
              className="flex-1 px-4 py-2 border border-dark-border rounded-md text-label hover:bg-dark-hover"
            >
              Retour
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-carmin text-white rounded-md hover:bg-carmin-light disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {submitting ? 'Publication...' : <><Check className="h-4 w-4" /> Publier</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToMarketplaceModal;
